<template>
  <div class="box variant-add-ons">
    <div class="section-header">
      <div class="section-label capitalize">
        {{ totalLabel }}
        <v-dropdown
          class="tooltip tooltip-variant--add-ons-overview"
          placement="top"
          :distance="22"
          :skidding="0"
        >
          <button
            class="tooltip-icon icon-info"
            role="presentation"
            :aria-label="totalLabel"
          />
          <template #popper>
            <div class="tooltip-variant--add-ons-overview tooltip-content">
              <div class="tooltip-header">
                <span
                  v-close-popper
                  :aria-label="tc('booking.close')"
                  tabindex="0"
                  class="icon-close"
                />
              </div>
              <div class="tooltip-body">
                {{ tooltipText }}
              </div>
            </div>
          </template>
        </v-dropdown>
      </div>
      <div class="section-total">
        {{ getFormattedPrice(addOnsTotal) }}
      </div>
    </div>
    <div class="section-header print-only">
      <div class="section-label capitalize">
        {{ printOnlyTotalLabel }}
      </div>
      <div class="section-total">
        {{ getFormattedPrice(addOnsTotal) }}
      </div>
    </div>
    <div class="overview-items-wrapper">
      <see-details>
        <template slot-scope="{ opened }">
          <div
            v-for="addOn in addOns"
            :key="addOn.synxisId"
            class="add-on-item"
            :class="{'mb-3': opened}"
          >
            <div
              class="overview-item"
              :class="{'mb-2': opened, 'mb-3': !opened}"
            >
              <span class="item label bold-item add-on__name">{{ addOn.addOnName }}</span>
              <span class="item-value bold-item add-on__total">{{ getAddOnTotalPrice(addOn) }}</span>
            </div>
            <transition name="expand-y">
              <template v-if="opened">
                <div class="overview-items-wrapper">
                  <div class="overview-item add-on__price-type">
                    <span class="section-label">{{ getPriceAndTypeLabel(addOn) }}</span>
                  </div>
                  <div class="overview-item add-on__quantity small-margin">
                    <span>
                      {{ quantityLabel }}: {{ addOn.quantity }}
                      <v-dropdown
                        v-if="addOn.excludeKidsFromPriceCalculations && tooltipMessage !== ''"
                        class="tooltip tooltip-variant--add-ons-overview"
                        :distance="25"
                        :skidding="0"
                        placement="top"
                      >
                      <button
                        class="tooltip-icon icon-info"
                        :aria-label="tooltipMessage"
                      />

                      <template #popper>
                        <div class="tooltip-variant--add-ons-overview tooltip-content">
                          <div class="tooltip-header">
                            <button
                              v-close-popper
                              :aria-label="tc('booking.close')"
                              tabindex="0"
                              class="icon-close"
                            />
                          </div>
                          <div class="tooltip-body">
                            {{ tooltipMessage }}
                          </div>
                        </div>
                      </template>
                    </v-dropdown>
                    </span>
                  </div>
                </div>
              </template>
            </transition>
          </div>
        </template>
      </see-details>
    </div>
    <div class="overview-items-wrapper print-only">
      <div
        v-for="addOn in addOns"
        :key="addOn.synxisId"
        class="add-on-item"
      >
        <div class="overview-item">
          <span class="item label bold-item add-on__name">{{ addOn.addOnName }}</span>
          <span class="item-value bold-item add-on__total">{{ getAddOnTotalPrice(addOn) }}</span>
        </div>
        <div class="overview-items-wrapper">
          <div class="overview-item add-on__price-type">
            <span class="section-label">{{ getPriceAndTypeLabel(addOn) }}</span>
          </div>
          <div class="overview-item add-on__quantity small-margin">
            <span>{{ quantityLabel }}: {{ addOn.quantity }}</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { mapGetters } from 'vuex'
  import { formatDateZone } from '@utils/date-helpers'
  import SeeDetails from '@components/04.booking/booking-wizard/components/see-details/see-details'

  export default {
    name: 'AddOnsOverview',
    inject: ['modifyAddOnsData'],
    components: {
      SeeDetails
    },
    props: {
      addOns: {
        type: Array,
        required: true
      },
      currencyCode: {
        type: String,
        required: true
      }
    },
    computed: {
      ...mapGetters('i18n', ['tc']),
      ...mapGetters('currency', ['getDisplayPrice', 'orSelected']),

      addOnsTotal() {
        let total = 0
        if (!Array.isArray(this.addOns)) {
          return 0
        }
        this.addOns.forEach((addOn) => {
          const prices = addOn.excludeKidsFromPriceCalculations ? addOn.totalPriceNoChildrenNoTax : addOn.totalPriceNoTax
          total += prices.reduce((sum, prc) => sum + prc, 0)
        })
        return total
      },
      quantityLabel() {
        return this.modifyAddOnsData?.quantityLabel || ''
      },
      totalLabel() {
        return this.modifyAddOnsData?.addonsTotalLabel || ''
      },
      printOnlyTotalLabel() {
        return this.modifyAddOnsData?.addonsTaxesTotalLabel || ''
      },
      tooltipText() {
        return this.modifyAddOnsData?.addonsTotalTooltipText || ''
      },
      tooltipMessage() {
        return this.modifyAddOnsData?.tooltipChildrenNotIncluded || ''
      }
    },
    methods: {
      getFormattedPrice(price) {
        return this.getDisplayPrice({
          price,
          fromCurrency: this.currencyCode,
          toCurrency:
            this.orSelected(this.currencyCode),
          withIsoCode: true,
          isoAfter: true,
        })
      },
      getAddOnTotalPrice(addOn) {
        const prices = addOn.excludeKidsFromPriceCalculations ? addOn.totalPriceNoChildrenNoTax : addOn.totalPriceNoTax
        const totalPrice = prices.reduce((sum, prc) => sum + prc, 0)

        return this.getFormattedPrice(totalPrice)
      },
      dateFormatted(date) {
        return formatDateZone(date, null, 'ddd, D MMM, YYYY')
      },
      getServicePricingLabel(key) {
        return this.modifyAddOnsData?.servicePricingType[key]
      },
      getPriceAndTypeLabel(addOn) {
        return addOn.airportAddOn
          ? this.getFormattedPrice(addOn.priceBeforeTax)
          : `${this.getFormattedPrice(addOn.priceBeforeTax)} / ${this.getServicePricingLabel(addOn.servicePricingTypeKey)}`
      }
    }
  }
</script>
